import { getContrastColor } from '@/lib/colorUtils';
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

interface AddPostByUrlProps {
  northId: string;
  onPostAdded: () => void;
  secondaryColor?: string | null;
}

export const AddPostByUrl = ({ northId, onPostAdded, secondaryColor }: AddPostByUrlProps) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url.trim()) {
      toast({
        title: "Error",
        description: "Please enter a post URL",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-social-post-by-url', {
        body: { url: url.trim(), northId }
      });

      if (error) {
        // Extract the actual error message from the response body
        let message = "Failed to add post. Please check the URL and try again.";
        try {
          const errorData = typeof error.context === 'object' && error.context?.json
            ? await error.context.json()
            : null;
          if (errorData?.error) {
            message = errorData.error;
          }
        } catch {}
        throw new Error(message);
      }

      if (!data.success) {
        throw new Error(data.error || "Failed to fetch post data");
      }

      toast({
        title: "Success",
        description: "Post added successfully",
      });

      setUrl("");
      setOpen(false);
      
      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['social-campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['social-media-posts'] });
      
      onPostAdded();
    } catch (error) {
      logger.error('Error adding post:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add post. Please check the URL and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="gap-2" style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}>
        <Plus className="h-4 w-4" />
        Add Post by URL
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Social Media Post</DialogTitle>
            <DialogDescription>
              Paste a link to an Instagram, Facebook, or YouTube post to add it to your report.
              The post must be public to be added.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="post-url">Post URL</Label>
                <Input
                  id="post-url"
                  placeholder="https://www.instagram.com/p/..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Supported platforms: Instagram, Facebook, YouTube
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Post
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};